import React, { Dispatch, SetStateAction } from 'react';
import Image from 'next/image';
import ReactDatePicker from '@common/ReactDatePicker/ReactDatePicker';
import ReactSelectSearch from '@common/ReactSelectSearch/ReactSelectSearch';
import locationPin from '@assets/images/location-pin-new.svg';
import calendarHeaderIcon from '@assets/images/calendar-header-icon-new.svg';
import { SingleValue } from 'react-select';
import { Location } from '@components/Hotels/types';
import useIsStickyHeaderVisible from '@hooks/useIsStickyHeaderVisible';
import useBannerMargin from '@hooks/useBannerMargin';

interface SearchOption {
  readonly value: string;
  readonly label: string;
}

type Props = {
  value: SingleValue<SearchOption> | undefined;
  setValue: Function;
  locations: Location[];
  setLocations: Dispatch<SetStateAction<Location[]>>;
  searchWithoutQuery: boolean;
  startDate: Date | undefined;
  setStartDate: Dispatch<SetStateAction<Date | undefined>> | ((date: Date | undefined) => void);
  isHeaderVisible: boolean;
  onSearchClick: () => void;
  input: any;
};

export default function StickySearchBar({
  value,
  setValue,
  locations,
  setLocations,
  input,
  searchWithoutQuery,
  startDate,
  setStartDate,
  isHeaderVisible,
  onSearchClick,
}: Props) {
  const isStickyVisible = useIsStickyHeaderVisible();

  const headerMarginTop = useBannerMargin();
  const searchBarMobileClasses = `${
    isHeaderVisible ? 'h-auto pb-4' : '-translate-y-4 pb-0'
  } px-4 flex flex-col items-center justify-center transform transition duration-300 ease-linear shadow-solar-search`;

  return (
    <div
      className={`${searchBarMobileClasses} d:h-78 d:flex-row d:pl-6 d:pr-5 d:py-4 d:shadow-none ${
        isStickyVisible ? 'fixed top-14 z-250 bg-white py-4 d:top-0 d:left-1/4 d:z-350' : ''
      }`}
      id="sticky-search-bar"
      style={{ marginTop: headerMarginTop }}
    >
      <div className="box-content bg-white h-13 flex flex-row items-center relative shadow-search-header w-full border border-solid border-rp-gray-border rounded-xl d:w-454 d:h-50">
        <div className="ml-1.5 mt-1 mr-1">
          <Image src={locationPin} height="28" width="28" alt="search-icon" />
        </div>
        <div className="w-1/2 ml-0 overflow-ellipsis d:w-3/5">
          <ReactSelectSearch
            value={value}
            setValue={setValue}
            locations={locations}
            setLocations={setLocations}
            input={input}
            headerVariant
            searchWithoutQuery={searchWithoutQuery}
            isShowLocationNearMe
            isNewHomePage
            scrollIntoView
          />
        </div>
        <div className="w-1/3 h-full mb-0 flex flex-row items-center pl-3 box-content mr-0 border-l border-solid border-rp-gray-divider srp-date-picker -ml-3 d:ml-0 d:border-r-0">
          <div className="box-content w-5 h-5 relative">
            <Image src={calendarHeaderIcon} layout="fill" alt="Calendar icon" />
          </div>
          <ReactDatePicker
            classes="z-100 pl-2 mb-1 text-15 d:text-base leading-4 font-rp-pn-regular text-custom-black"
            startDate={startDate}
            setStartDate={setStartDate}
            showClearDate
            isShortDateFormat
          />
        </div>
        <button
          type="button"
          className="w-24 px-3 flex justify-center items-center bg-rp-primary hover:bg-rp-primary-light active:bg-rp-primary-dark py-4 rounded-r-xl text-white font-rp-pn-semi-bold tracking-wide -mr-px"
          onClick={onSearchClick}
          style={{ height: 52 }}
        >
          Search
        </button>
      </div>
    </div>
  );
}
